import type { CanvasEdge, CanvasNode } from "@opus-dt/shared-types";

export type ValidationError =
  | { kind: "MACHINE_MISSING_ID"; nodeId: string; label: string }
  | { kind: "DUPLICATE_MACHINE"; machineId: string }
  | { kind: "EDGE_FROM_END"; edgeId: string }
  | { kind: "EDGE_TO_START"; edgeId: string }
  | { kind: "DUPLICATE_EDGE"; from: string; to: string; type: string }
  | { kind: "EDGE_DANGLING"; edgeId: string };

export type ValidationWarning =
  | { kind: "CYCLE"; nodes: string[] }
  | { kind: "ORPHAN_NODE"; nodeId: string; label: string };

export interface ValidationResult {
  errors: ValidationError[];
  warnings: ValidationWarning[];
  canSave: boolean;
}

export function validateCanvas(nodes: CanvasNode[], edges: CanvasEdge[]): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];
  const byId = new Map(nodes.map((n) => [n.id, n] as const));

  for (const n of nodes) {
    if (n.type === "MACHINE") {
      const mid = n.data?.machineId;
      if (typeof mid !== "string" || mid.length === 0) {
        errors.push({ kind: "MACHINE_MISSING_ID", nodeId: n.id, label: n.label });
      }
    }
  }

  const seenMachineIds = new Map<string, number>();
  for (const n of nodes) {
    if (n.type !== "MACHINE") continue;
    const mid = n.data?.machineId;
    if (typeof mid !== "string") continue;
    seenMachineIds.set(mid, (seenMachineIds.get(mid) ?? 0) + 1);
  }
  for (const [mid, count] of seenMachineIds) {
    if (count > 1) errors.push({ kind: "DUPLICATE_MACHINE", machineId: mid });
  }

  const seenEdges = new Set<string>();
  for (const e of edges) {
    const src = byId.get(e.sourceNodeId);
    const tgt = byId.get(e.targetNodeId);
    if (!src || !tgt) {
      errors.push({ kind: "EDGE_DANGLING", edgeId: e.id });
      continue;
    }
    if (src.type === "END") errors.push({ kind: "EDGE_FROM_END", edgeId: e.id });
    if (tgt.type === "START") errors.push({ kind: "EDGE_TO_START", edgeId: e.id });
    const key = `${e.sourceNodeId}|${e.targetNodeId}|${e.type}`;
    if (seenEdges.has(key)) {
      errors.push({
        kind: "DUPLICATE_EDGE",
        from: e.sourceNodeId,
        to: e.targetNodeId,
        type: e.type,
      });
    }
    seenEdges.add(key);
  }

  const adj = new Map<string, string[]>();
  for (const e of edges) {
    if (e.type !== "PRODUCTION_FLOW") continue;
    const arr = adj.get(e.sourceNodeId) ?? [];
    arr.push(e.targetNodeId);
    adj.set(e.sourceNodeId, arr);
  }
  const visited = new Set<string>();
  const stack = new Set<string>();
  function dfs(id: string, path: string[]): string[] | null {
    if (stack.has(id)) {
      const i = path.indexOf(id);
      return path.slice(i).concat(id);
    }
    if (visited.has(id)) return null;
    visited.add(id);
    stack.add(id);
    for (const next of adj.get(id) ?? []) {
      const cycle = dfs(next, [...path, id]);
      if (cycle) return cycle;
    }
    stack.delete(id);
    return null;
  }
  for (const n of nodes) {
    const cycle = dfs(n.id, []);
    if (cycle) {
      warnings.push({ kind: "CYCLE", nodes: cycle });
      break;
    }
  }

  if (nodes.length > 1) {
    const linked = new Set<string>();
    for (const e of edges) {
      linked.add(e.sourceNodeId);
      linked.add(e.targetNodeId);
    }
    for (const n of nodes) {
      if (n.type === "START" || n.type === "END") continue;
      if (!linked.has(n.id)) {
        warnings.push({ kind: "ORPHAN_NODE", nodeId: n.id, label: n.label });
      }
    }
  }

  return {
    errors,
    warnings,
    canSave: errors.length === 0,
  };
}
